// Data Lab · render-time normalisation helper (Module 06 §9.2).
// PURE functions over a COPY of the doc. NEVER mutates stored MongoDB data.
import {
  PERSON_PROVENANCE, CRM_ENRICHMENT_FIELDS,
} from "../../config/datalabProvenance";

export function isEmpty(v) {
  if (v == null) return true;
  if (v === "" || v === "—") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false; // numbers (incl. 0) are real data in a QA inspector
}

// "£555.25" / "1,827.92" → 555.25 / 1827.92 ; numbers pass through; else null.
export function coerceMoney(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string" && /\d/.test(v)) {
    const n = Number(v.replace(/[£$,\s]/g, ""));
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

const UK_PC_RE = /[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}/gi;

function noteContent(n) {
  if (n == null) return "";
  if (typeof n === "string") return n;
  return n.Content ?? n.content ?? n.note ?? n.Note ?? "";
}

// Dedup notes by content; keep a "seen N×", type + date. Newest first.
export function dedupeNotes(notes) {
  const map = new Map();
  for (const n of notes || []) {
    const content = String(noteContent(n)).trim();
    if (!content) continue;
    if (map.has(content)) { map.get(content).seen += 1; continue; }
    map.set(content, {
      content,
      type: (n && (n.Type || n.type || n.NoteType || n.Source)) || "note",
      date: (n && (n.GeneratedDate || n.ReconDate || n.Date || n.add_time || n.created)) || null,
      seen: 1,
    });
  }
  return [...map.values()].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

// Deterministic contamination check: does the note reference a UK postcode that
// disagrees with the record's own postcode?
export function isContaminated(note, doc) {
  const own = String(doc.PostCode || doc.Postcode || "").replace(/\s/g, "").toLowerCase();
  if (!own) return false;
  const found = (String(note.content || "").match(UK_PC_RE) || [])
    .map((s) => s.replace(/\s/g, "").toLowerCase());
  return found.length > 0 && !found.includes(own);
}

export function hasAiField(doc) {
  return Object.keys(doc || {}).some((k) => k.toLowerCase().startsWith("_ai"));
}

// Completeness bar per provenance class (fields present on the doc).
function classBars(doc, classes = ["bc", "crm", "sys"]) {
  return classes.map((cls) => {
    const fields = Object.keys(PERSON_PROVENANCE).filter(
      (f) => PERSON_PROVENANCE[f] === cls && f in doc
    );
    const filled = fields.filter((f) => !isEmpty(doc[f])).length;
    return { cls, filled, total: fields.length, pct: fields.length ? Math.round((100 * filled) / fields.length) : 0 };
  });
}

// Full person health scorecard (Module 06 §3). Deterministic, no AI.
export function personHealth(doc) {
  const issues = [];
  const rawNotes = Array.isArray(doc.Notes) ? doc.Notes : [];
  const unique = dedupeNotes(rawNotes);
  const contaminatedKeys = new Set(unique.filter((n) => isContaminated(n, doc)).map((n) => n.content));

  if (rawNotes.length > unique.length) {
    issues.push({
      sev: "med",
      text: `Duplicate notes — ${rawNotes.length} entries, only ${unique.length} unique`,
      route: "→ deduped at render time · upstream fix owner: Mohammed / Raj",
    });
  }
  if (contaminatedKeys.size > 0) {
    issues.push({
      sev: "high",
      text: `Cross-customer contamination — ${contaminatedKeys.size} note(s) reference a different customer`,
      route: "→ decision for Raj (bad merge / mis-tagged RECON — flagged, not hidden)",
    });
  }

  const filledCrm = CRM_ENRICHMENT_FIELDS.filter((f) => !isEmpty(doc[f.key])).length;
  const crmPct = Math.round((100 * filledCrm) / CRM_ENRICHMENT_FIELDS.length);
  if (crmPct < 50) {
    issues.push({
      sev: "med",
      text: `CRM completeness low — ${filledCrm}/${CRM_ENRICHMENT_FIELDS.length} enrichment fields populated`,
      route: "→ Mohammed / Raj (enrichment / 360 targets)",
    });
  }

  const ai = hasAiField(doc);
  if (!ai) {
    issues.push({
      sev: "info",
      text: "No AI enrichment (_ai_person) present on this record",
      route: "→ Phase 2 · get to the bottom of it: Mohammed / Raj",
    });
  }

  const state = String(doc.State || "").toLowerCase().replace(/\s/g, "");
  const city = String(doc.City || "").toLowerCase().replace(/\s/g, "");
  if (state && city && (state.startsWith(city.slice(0, 4)) || city.startsWith(state.slice(0, 4)))) {
    issues.push({
      sev: "low",
      text: `Schema/typo — State "${doc.State}" looks like a duplicate of City "${doc.City}"`,
      route: "→ upstream schema cleanup (low)",
    });
  }
  if (!isEmpty(doc.Data8Errors)) {
    issues.push({ sev: "med", text: "Data8 address validation errors present", route: "→ address QA" });
  }

  const bars = classBars(doc);
  bars.push({ cls: "crm-enrich", filled: filledCrm, total: CRM_ENRICHMENT_FIELDS.length, pct: crmPct });

  const grade = issues.some((i) => i.sev === "high" || i.sev === "med") ? "NEEDS ATTENTION" : "HEALTHY";

  return { issues, bars, grade, uniqueNotes: unique, contaminatedKeys, aiPresent: ai, crmPct };
}
